/**
 * Tamper / anti-spoof collector (P2).
 *
 * Heuristic detection of automation, headless runtimes, emulators, function
 * tampering, UA/platform mismatch, private mode, screen mirroring and remote
 * desktop. Each check is best-effort and degrades to `null`/`false` rather
 * than throwing. Results feed the server-side scoring engine — they are signals,
 * not verdicts.
 */

import type { TamperSignals } from "../types.ts";
import { sha256Short } from "../util/sha256.ts";
import { tryCatch } from "../util/async.ts";

/* webdriver / automation flags */
function detectWebdriver(): boolean {
  return tryCatch(() => navigator.webdriver === true, false);
}

function automationFlags(): string[] {
  const flags: string[] = [];
  const w = window as unknown as Record<string, unknown>;
  const probes: Array<[string, () => boolean]> = [
    ["webdriver", () => navigator.webdriver === true],
    ["cdc_props", () => Object.keys(w).some((k) => /^[$_]?cdc_/.test(k) || /\$cdc_/.test(k))],
    ["__webdriver_evaluate", () => "__webdriver_evaluate" in w || "__driver_evaluate" in w],
    ["__selenium", () => "__selenium_unwrapped" in w || "__webdriver_script_fn" in w],
    ["__nightmare", () => "__nightmare" in w],
    ["_phantom", () => "_phantom" in w || "callPhantom" in w],
    ["domAutomation", () => "domAutomation" in w || "domAutomationController" in w],
    ["puppeteer", () => "__puppeteer_evaluation_script__" in w],
    ["playwright", () => "__playwright" in w || "__pw_manual" in w],
  ];
  for (const [name, fn] of probes) {
    if (tryCatch(fn, false)) flags.push(name);
  }
  return flags;
}

/* headless heuristics */
function detectHeadless(flags: string[]): boolean {
  return tryCatch(() => {
    if (flags.length > 0) return true;
    const ua = navigator.userAgent || "";
    if (/HeadlessChrome|PhantomJS|Electron/i.test(ua)) return true;
    // Headless Chrome historically reports 0 plugins + missing languages.
    const noPlugins = (navigator.plugins?.length ?? 0) === 0;
    const noLanguages = !navigator.languages || navigator.languages.length === 0;
    // Permissions API quirk: notifications "denied" while Notification "default".
    return noPlugins && noLanguages;
  }, false);
}

/* emulator / touch-UA mismatch */
function detectEmulator(): boolean {
  return tryCatch(() => {
    const ua = navigator.userAgent || "";
    const mobileUa = /Mobi|Android|iPhone|iPad/i.test(ua);
    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    // A "mobile" UA on a device reporting no touch + desktop concurrency.
    if (mobileUa && !hasTouch) return true;
    return false;
  }, false);
}

/* native function tampering (toString patched) */
function detectFunctionTampering(): boolean {
  return tryCatch(() => {
    const natives = [
      Function.prototype.bind,
      navigator.permissions?.query,
      HTMLCanvasElement.prototype.toDataURL,
      WebGLRenderingContext.prototype.getParameter,
    ].filter(Boolean) as Array<(...a: unknown[]) => unknown>;
    for (const fn of natives) {
      const s = Function.prototype.toString.call(fn);
      if (!/\{\s*\[native code\]\s*\}/.test(s)) return true;
    }
    // toString itself patched?
    const ts = Function.prototype.toString.toString();
    if (!/\{\s*\[native code\]\s*\}/.test(ts)) return true;
    return false;
  }, false);
}

/* UA-platform mismatch (navigator.platform vs UA family) */
function detectUaPlatformMismatch(): boolean {
  return tryCatch(() => {
    const ua = (navigator.userAgent || "").toLowerCase();
    const plat = (navigator.platform || "").toLowerCase();
    if (!plat) return false;
    const uaWin = ua.includes("windows");
    const uaMac = ua.includes("mac os") || ua.includes("macintosh");
    const uaLinux = ua.includes("linux") && !ua.includes("android");
    const platWin = plat.includes("win");
    const platMac = plat.includes("mac");
    const platLinux = plat.includes("linux") || plat.includes("x11");
    if (uaWin && !platWin) return true;
    if (uaMac && !platMac) return true;
    if (uaLinux && !platLinux) return true;
    return false;
  }, false);
}

/* private / incognito (best-effort, storage-quota based) */
async function detectPrivateMode(): Promise<boolean | null> {
  return await new Promise<boolean | null>((resolve) => {
    try {
      const est = (navigator as unknown as { storage?: { estimate?: () => Promise<{ quota?: number }> } }).storage;
      if (est?.estimate) {
        est.estimate().then((e) => {
          // Incognito Chromium caps quota well under normal (~120MB) values.
          if (typeof e.quota === "number") resolve(e.quota < 120_000_000);
          else resolve(null);
        }).catch(() => resolve(null));
        setTimeout(() => resolve(null), 500);
        return;
      }
      resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/* screen mirroring / remote desktop heuristics */
function detectScreenMirrored(): boolean | null {
  return tryCatch<boolean | null>(() => {
    // Mismatched avail vs total or zero color depth often appears under
    // mirroring / RDP virtual displays.
    if (screen.colorDepth && screen.colorDepth <= 16) return true;
    if (screen.availWidth > screen.width || screen.availHeight > screen.height) return true;
    return false;
  }, null);
}

function detectRemoteDesktop(): boolean | null {
  return tryCatch<boolean | null>(() => {
    // RDP/virtual GPUs commonly report a 0/odd pixel ratio with low depth.
    const dpr = window.devicePixelRatio;
    if ((dpr === 0 || !dpr) && screen.colorDepth <= 16) return true;
    return false;
  }, null);
}

export async function collectTamper(): Promise<TamperSignals> {
  const flags = automationFlags();
  const privateMode = await detectPrivateMode();

  const isWebdriver = detectWebdriver();
  const isHeadless = detectHeadless(flags);
  const isEmulator = detectEmulator();
  const functionTampered = detectFunctionTampering();
  const uaMismatch = detectUaPlatformMismatch();
  const screenMirrored = detectScreenMirrored();
  const remoteDesktop = detectRemoteDesktop();

  return {
    is_webdriver: isWebdriver,
    is_headless: isHeadless,
    is_emulator: isEmulator,
    function_tampered: functionTampered,
    ua_platform_mismatch: uaMismatch,
    private_mode: privateMode,
    automation_flags: flags,
    screen_mirrored: screenMirrored,
    remote_desktop_suspected: remoteDesktop,
  };
}

/** Stable hash summarising the tamper posture for the device's spoofing_hash. */
export async function spoofingHash(t: TamperSignals): Promise<string> {
  const seed = [
    t.is_webdriver, t.is_headless, t.is_emulator, t.function_tampered,
    t.ua_platform_mismatch, t.private_mode, t.automation_flags.join(","),
    t.screen_mirrored, t.remote_desktop_suspected,
  ].join("~");
  return await sha256Short(seed);
}
